import { useState } from 'react'
import Box from '@mui/material/Box'
import type { PlatformDashboardGrowthPeriod } from '@brushfire/types'
import {
  usePlatformPeopleAccountsDashboard,
  usePrefetchPlatformPeopleAccountsDashboard,
} from '../api/platform-dashboard-queries'
import { PlatformDashboardTabs } from './PlatformDashboardTabs'
import { PlatformPopulationTiles, PlatformGrowthTiles } from './PlatformMetricTiles'
import { GrowthTrendChart } from './GrowthTrendChart'
import { EkycStatusPlaceholder } from './EkycStatusPlaceholder'
import { SignupSourceSplitChart } from './SignupSourceSplitChart'
import { DemographicCompletenessChart } from './DemographicCompletenessChart'
import { NewSignupsByDayChart } from './NewSignupsByDayChart'
import { PlatformPeopleAccountsSkeleton } from './skeletons/PlatformPeopleAccountsSkeleton'
import { ErrorState } from '@/shared/components/ErrorState'

export function PeopleAccountsTab() {
  const [growthPeriod, setGrowthPeriod] = useState<PlatformDashboardGrowthPeriod>('week')
  usePrefetchPlatformPeopleAccountsDashboard()
  const { data, isLoading, isError, refetch, isPlaceholderData } =
    usePlatformPeopleAccountsDashboard(growthPeriod)

  if (isLoading) {
    return <PlatformPeopleAccountsSkeleton />
  }

  if (isError || !data) {
    return <ErrorState onRetry={() => void refetch()} />
  }

  return (
    <Box data-testid="platform-people-accounts-tab">
      <PlatformDashboardTabs activeTab="people" />
      <PlatformPopulationTiles data={data} />
      <PlatformGrowthTiles
        data={data}
        growthPeriod={growthPeriod}
        onGrowthPeriodChange={setGrowthPeriod}
        isPeriodRefreshing={isPlaceholderData}
      />
      <Box sx={{ mb: 3 }}>
        <GrowthTrendChart
          trend={data.growthTrend}
          growthPeriod={growthPeriod}
          onGrowthPeriodChange={setGrowthPeriod}
          isPeriodRefreshing={isPlaceholderData}
        />
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
          gap: 3,
          mb: 3,
        }}
      >
        <EkycStatusPlaceholder />
        <SignupSourceSplitChart segments={data.signupSourceSplit} />
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
          gap: 3,
        }}
      >
        <DemographicCompletenessChart rows={data.demographicCompleteness} />
        <NewSignupsByDayChart newSignupsByDay={data.newSignupsByDay} />
      </Box>
    </Box>
  )
}
